import { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// True when rendered inside another TabCardPanel. A nested panel defaults to non-sticky so two tab
// rows don't both fight for `top-0` of the page scroller (#5348). Explicit `stickyTabs` wins.
const TabCardPanelNestingContext = createContext(false);

export interface Tab {
  id: string;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  /** Render a custom element instead of a button (e.g., Popover) */
  render?: () => ReactNode;
}

export interface TabCardPanelProps {
  /** Array of tabs to display */
  tabs: Tab[];
  /** Content to render inside the card */
  children: ReactNode;
  /** Optional actions to display on the right side of the tab bar */
  actions?: ReactNode;
  /** Additional className for the card */
  cardClassName?: string;
  /** Additional className for the card content */
  contentClassName?: string;
  /** Whether to remove padding from CardContent (default: false) */
  noPadding?: boolean;
  /**
   * Pin the tab + actions row to the top of the page scroller so the tabs and the Save button stay
   * visible while the content scrolls beneath them, with a subtle drop shadow once scrolled (#5348).
   * Default `true`. Set `false` for nested panels or panels that don't live in the main page
   * scroller (e.g. inside a modal/side panel), where a second sticky row would fight for `top-0`.
   */
  stickyTabs?: boolean;
}

/**
 * TabCardPanel - A reusable component for tab-attached-to-card layouts
 *
 * This component provides a consistent pattern for panels with tabs that
 * visually connect to a card below them. The active tab appears to be
 * part of the card while inactive tabs appear separate.
 *
 * @example
 * // Basic usage with static tab
 * <TabCardPanel
 *   tabs={[{ id: 'all', label: 'All Items', isActive: true }]}
 * >
 *   <ItemsList />
 * </TabCardPanel>
 *
 * @example
 * // With multiple tabs and actions
 * <TabCardPanel
 *   tabs={[
 *     { id: 'all', label: 'All Campaigns', isActive: true },
 *     { id: 'new', label: 'New', onClick: () => navigate('/new') },
 *   ]}
 *   actions={<Button>Export</Button>}
 * >
 *   <CampaignsList />
 * </TabCardPanel>
 *
 * @example
 * // With custom tab render (e.g., Popover)
 * <TabCardPanel
 *   tabs={[
 *     { id: 'all', label: 'All Reports', isActive: true },
 *     {
 *       id: 'new',
 *       label: 'New',
 *       render: () => (
 *         <Popover>
 *           <PopoverTrigger asChild>
 *             <button className="...">New</button>
 *           </PopoverTrigger>
 *           <PopoverContent>...</PopoverContent>
 *         </Popover>
 *       )
 *     },
 *   ]}
 * >
 *   <ReportsList />
 * </TabCardPanel>
 */
export function TabCardPanel({
  tabs,
  children,
  actions,
  cardClassName,
  contentClassName,
  noPadding = false,
  stickyTabs,
}: TabCardPanelProps) {
  // Default: stick top-level panels, but not nested ones (a nested panel would fight the outer for
  // top-0). An explicit `stickyTabs` overrides. #5348.
  const isNested = useContext(TabCardPanelNestingContext);
  const sticky = stickyTabs ?? !isNested;

  // Drop a 0-height sentinel just above the (sticky) tab row; once it scrolls out of view the row is
  // pinned, so we add the drop shadow. #5348.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!sticky) return;
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sticky]);

  return (
    <TabCardPanelNestingContext.Provider value={true}>
    <div>
      {sticky && <div ref={sentinelRef} aria-hidden className="h-px w-full" />}
      {/* Tab Navigation - Outside Card (pinned to the top of the page scroller when sticky) */}
      <div
        className={cn(
          'flex items-end justify-between',
          sticky && 'sticky top-0 z-20 bg-[#fafafb] transition-shadow duration-200',
          sticky && stuck && 'shadow-[0_6px_8px_-6px_rgba(15,23,42,0.18)]',
        )}
      >
        <div className="flex items-end gap-1">
          {tabs.map((tab) => {
            // If tab has custom render, use that
            if (tab.render) {
              return <div key={tab.id}>{tab.render()}</div>;
            }

            // Otherwise render standard tab button
            return (
              <button
                key={tab.id}
                onClick={tab.onClick}
                className={cn(
                  'relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all',
                  tab.isActive
                    ? 'bg-white text-gray-900 border border-gray-200 border-b-transparent -mb-px'
                    : 'bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200 hover:text-gray-900',
                  tab.onClick && 'cursor-pointer'
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {actions && (
          <div className="flex items-center gap-2 pb-1">
            {actions}
          </div>
        )}
      </div>
      {/* Card with Content. The card's own (rounded) top border is the tab-strip rule: the active tab
          reaches 1px down over it (-mb-px) to merge in, inactive tabs sit on it, and the region right
          of the tabs keeps it. No -mt-px — tucking the card up under the sticky row's background is
          what hid this border to the right of the tabs. */}
      <Card className={cn('rounded-lg rounded-tl-none border bg-white overflow-hidden', cardClassName)}>
        <CardContent className={cn(noPadding ? 'p-0' : 'p-6', contentClassName)}>
          {children}
        </CardContent>
      </Card>
    </div>
    </TabCardPanelNestingContext.Provider>
  );
}

/**
 * Helper to create a standard tab button style class string
 * Use this when you need the tab styling but can't use the TabCardPanel component
 */
export const tabButtonClass = (isActive: boolean) =>
  cn(
    'relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all',
    isActive
      ? 'bg-white text-gray-900 border border-gray-200 border-b-transparent -mb-px'
      : 'bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200 hover:text-gray-900'
  );
